// frontend/components/features/CredentialCard.tsx
"use client";

import { useState } from "react";
import {
  ExternalLink,
  Trash2,
  Edit2,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  deleteCredential,
  type NurseCredential,
  type CredentialStatus,
  type ExpiryStatus,
} from "@/lib/hooks/useNurseCredentials";
import { useToast } from "@/components/providers/ToastProvider";

// =============================================================================
// Status Badge Components
// =============================================================================

function CredentialStatusBadge({ status }: { status: CredentialStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "VERIFIED" && "bg-emerald-100 text-emerald-700",
        status === "PENDING" && "bg-amber-100 text-amber-700",
        status === "REJECTED" && "bg-red-100 text-red-700",
        status === "EXPIRED" && "bg-slate-100 text-slate-700"
      )}
    >
      {status === "VERIFIED" && <CheckCircle className="h-3 w-3" />}
      {status === "PENDING" && <Clock className="h-3 w-3" />}
      {status === "REJECTED" && <XCircle className="h-3 w-3" />}
      {status === "EXPIRED" && <AlertTriangle className="h-3 w-3" />}
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function ExpiryStatusBadge({ status }: { status: ExpiryStatus }) {
  if (status === "VALID") return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "EXPIRING_SOON" && "bg-amber-100 text-amber-700",
        status === "EXPIRED" && "bg-red-100 text-red-700"
      )}
    >
      {status === "EXPIRING_SOON" && <AlertTriangle className="h-3 w-3" />}
      {status === "EXPIRED" && <XCircle className="h-3 w-3" />}
      {status === "EXPIRING_SOON" ? "Expiring Soon" : "Expired"}
    </span>
  );
}

// =============================================================================
// Delete Confirmation Dialog
// =============================================================================

interface DeleteConfirmationProps {
  credentialName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmation({ credentialName, onConfirm, onCancel }: DeleteConfirmationProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-200">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-red-100 p-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">Delete Credential</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete <strong>{credentialName}</strong>? This action cannot
              be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Component Props
// =============================================================================

export interface CredentialCardProps {
  credential: NurseCredential;
  onView?: (credential: NurseCredential) => void;
  onEdit?: (credential: NurseCredential) => void;
  onDelete?: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function CredentialCard({ credential, onView, onEdit, onDelete }: CredentialCardProps) {
  const { showToast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [_isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteCredential(credential.id);
      showToast({
        variant: "success",
        title: "Credential deleted",
        description: `${credential.credential_type.name} has been removed.`,
      });
      setShowDeleteConfirm(false);
      onDelete?.();
    } catch (error) {
      showToast({
        variant: "error",
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete credential",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <div className="rounded-lg border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-slate-900">
                {credential.credential_type.name}
              </h3>
              <CredentialStatusBadge status={credential.status} />
              <ExpiryStatusBadge status={credential.expiry_status} />
            </div>

            {/* Metadata */}
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              {credential.license_number && (
                <div>
                  <span className="font-medium">Number:</span> {credential.license_number}
                </div>
              )}
              {credential.issuing_state && (
                <div>
                  <span className="font-medium">State:</span> {credential.issuing_state}
                </div>
              )}
              {credential.issued_at && (
                <div>
                  <span className="font-medium">Issued:</span> {formatDate(credential.issued_at)}
                </div>
              )}
              {credential.expires_at && (
                <div
                  className={cn(
                    credential.expiry_status === "EXPIRED" && "text-red-600 font-medium",
                    credential.expiry_status === "EXPIRING_SOON" && "text-amber-600 font-medium"
                  )}
                >
                  <span className="font-medium">Expires:</span>{" "}
                  {formatDate(credential.expires_at)}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {onView && (
              <button
                type="button"
                onClick={() => onView(credential)}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                title="View details"
              >
                <Eye className="h-4 w-4" />
              </button>
            )}
            {credential.document_url && (
              <a
                href={credential.document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                title="Open document"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(credential)}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                title="Edit metadata"
              >
                <Edit2 className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded p-1.5 text-slate-400 hover:bg-red-100 hover:text-red-600 transition-colors"
              title="Delete credential"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Description (if available) */}
        {credential.credential_type.description && (
          <p className="mt-3 text-xs text-slate-500 italic">
            {credential.credential_type.description}
          </p>
        )}

        {/* Required Badge */}
        {credential.credential_type.required_for_work && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <span className="inline-flex items-center gap-1 text-xs text-slate-600">
              <CheckCircle className="h-3 w-3 text-emerald-600" />
              Required for work
            </span>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmation
          credentialName={credential.credential_type.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </>
  );
}
